/**
 * CommonInner — 페이지 섹션 공통 내부 컨테이너
 *
 * 섹션마다 동일한 max-width / 좌우 패딩 / 상하 여백을 유지하는 레이아웃 래퍼.
 * 모든 섹션의 콘텐츠는 이 컴포넌트로 감싸는 것이 기본 규칙.
 *
 * [Props]
 * - `as`: 렌더링할 HTML 태그 (기본값: "div")
 *   "div" | "section" | "article" | "main" | "aside" | "header" | "footer"
 * - `direction`: 자식 요소 배치 방향 (기본값: "column")
 *   "column" → 세로 배치 / "row" → 가로 배치 / "row-reverse" → 가로 역순 배치 (PC에서만 전환)
 * - `maxWidth`: px 기준 최대 너비 (기본: PC 1268 / MO 780)
 *   단일값은 PC만 변경 / [PC, MO]는 각각 변경 (배열은 두 값 필수)
 * - `align`: align-items 정렬 (기본: "stretch", 텍스트 정렬 아님)
 *   단일값은 PC/MO 공통 적용 / [PC, MO]는 각각 적용 (배열은 두 값 필수)
 *   column에서는 가로축, row에서는 세로축 정렬
 * - `className`: 추가 클래스명 (섹션별 커스텀 스타일 적용 시 사용)
 * - 그 외 `HTMLAttributes<HTMLElement>` 속성 전부 지원 (id, style, onClick 등)
 *
 * [SCSS 커스텀]
 * 상하 패딩은 CSS 변수로 재정의 가능:
 * ```scss
 * .my_section_inner {
 *   --common-inner-space-y: var(--space-80); // 기본: PC 160 / MO 96
 * }
 * ```
 *
 * [사용 예]
 * ```tsx
 * // 기본 (세로 배치)
 * <CommonInner>
 *   <TitleGroup>...</TitleGroup>
 *   <CardList>...</CardList>
 * </CommonInner>
 *
 * // 가로 배치 (PC), section 태그로 렌더
 * <CommonInner as="section" direction="row">
 *   <LeftContent />
 *   <RightContent />
 * </CommonInner>
 *
 * // 섹션별 상하 여백 조정
 * <CommonInner className={s.my_inner}>...</CommonInner>
 * // .my_inner { --common-inner-space-y: var(--space-80); }
 *
 * // PC만 너비 변경, PC/MO 공통 중앙 정렬
 * <CommonInner maxWidth={1100} align="center">...</CommonInner>
 *
 * // PC/MO 각각 너비와 정렬 지정
 * <CommonInner maxWidth={[1100, 640]} align={['center', 'stretch']}>...</CommonInner>
 * ```
 */
import clsx from 'clsx';
import type { CSSProperties, HTMLAttributes } from 'react';
import { resolveResponsiveValue } from '@publishing/helpers/responsive';
import styles from './CommonInner.module.scss';

type CommonInnerAlign = 'stretch' | 'flex-start' | 'center' | 'flex-end' | 'baseline';

interface CommonInnerProps extends HTMLAttributes<HTMLElement> {
  as?: 'div' | 'section' | 'article' | 'main' | 'aside' | 'header' | 'footer';
  direction?: 'column' | 'row' | 'row-reverse';
  maxWidth?: number | readonly [pc: number, mo: number];
  align?: CommonInnerAlign | readonly [pc: CommonInnerAlign, mo: CommonInnerAlign];
}

export function CommonInner({
  children,
  className,
  as: Tag = 'div',
  direction = 'column',
  maxWidth,
  align,
  style,
  ...rest
}: CommonInnerProps) {
  const innerClassName = clsx(styles.inner, direction === 'row' && styles.row, direction === 'row-reverse' && styles.row_reverse, align && styles.aligned, className);
  const [pcMaxWidth, moMaxWidth] = resolveResponsiveValue(maxWidth);
  const [pcAlign, moAlign = pcAlign] = resolveResponsiveValue(align);
  const innerStyle = {
    '--common-inner-max-width-pc': pcMaxWidth === undefined ? undefined : `${pcMaxWidth / 16}rem`,
    '--common-inner-max-width-mo': moMaxWidth === undefined ? undefined : `${moMaxWidth / 16}rem`,
    '--common-inner-align-pc': pcAlign,
    '--common-inner-align-mo': moAlign,
    ...style,
  } as CSSProperties;

  return (
    <Tag
      className={innerClassName}
      style={innerStyle}
      {...rest}
    >
      {children}
    </Tag>
  );
}
